import {
  type HttpClient,
  parseContentDispositionFileName,
  type RequestConfig,
} from '@mathflat/domain/http/client'
import type { AxiosInstance } from 'axios'

import type { ApiEventName } from '@/utils/trackings/schema/apiEvent.schema'

// forwardMeta 로 쿼리 파라미터가 된 meta 는 응답 인터셉터가 이벤트 이름으로 읽으므로 카탈로그 이름만 허용한다
declare module '@mathflat/domain/http/client' {
  interface RequestMetaRegistry {
    meta: { mp_event_name: ApiEventName }
  }
}

// domain 의 HttpClient 는 응답 본문으로 resolve 하고, axios 인스턴스는 응답 객체를 돌려주므로 여기서 벗긴다.
// forwardMeta 를 켜면 meta 가 쿼리 파라미터가 되고, 응답 인터셉터가 그 값으로 트래킹한다.
export const toDomainHttpClient = (
  instance: AxiosInstance,
  { forwardMeta }: { forwardMeta: boolean },
): HttpClient => {
  const toAxiosConfig = (
    {
      params,
      data,
      meta,
      signal,
      withoutAuth,
      headers,
      preventErrorToast,
      trackingProps,
    }: RequestConfig = {},
    body?: unknown,
  ) => {
    // 인스턴스 기본 Content-Type 이 JSON 이면 axios 가 FormData 를 JSON 문자열로 바꿔 보낸다
    const mergedHeaders = {
      ...headers,
      ...(body instanceof FormData && { 'Content-Type': 'multipart/form-data' }),
    }
    return {
      params: forwardMeta && meta ? { ...(params as Record<string, unknown>), ...meta } : params,
      data,
      signal,
      ...(Object.keys(mergedHeaders).length > 0 && { headers: mergedHeaders }),
      ...(withoutAuth && { withoutAuth }),
      // 이 앱은 오류 코드별로 토스트를 가르지 못한다. 전부 막는 경우만 옮기고, 그것을 읽는 인스턴스는 identifyAxios 뿐이다
      ...(preventErrorToast === true && { skipErrorToast: true }),
      ...(trackingProps && { mpEventProps: trackingProps }),
    }
  }

  return {
    get: (url, config) => instance.get(url, toAxiosConfig(config)).then((res) => res.data),
    post: (url, body, config) =>
      instance.post(url, body, toAxiosConfig(config, body)).then((res) => res.data),
    put: (url, body, config) =>
      instance.put(url, body, toAxiosConfig(config, body)).then((res) => res.data),
    patch: (url, body, config) =>
      instance.patch(url, body, toAxiosConfig(config, body)).then((res) => res.data),
    delete: (url, config) => instance.delete(url, toAxiosConfig(config)).then((res) => res.data),
    getFile: (url, config) =>
      instance.get<Blob>(url, { ...toAxiosConfig(config), responseType: 'blob' }).then((res) => ({
        blob: res.data,
        fileName: parseContentDispositionFileName(res.headers['content-disposition']),
      })),
  }
}
